import React, { FC, RefObject, useState } from 'react'

export interface ProgressBarPresentationProps {
  progress: number
  buffered?: number
  currentTimeDisplay: string
  durationDisplay: string
  /** 残り時間。分割表示の右側に出す */
  remainingTimeDisplay?: string
  showTimeStamps?: boolean
  isSeekable?: boolean
  onClick: (e: React.MouseEvent) => void
  onMouseDown: (e: React.MouseEvent) => void
  seekBarRef?: RefObject<HTMLDivElement | null>
}

/**
 * 動画上端の進捗バー（Instagram ストーリー風）。
 *
 * シーク可能な操作なので、他のプレイヤー（YouTube 等）やワイド型の WideControlsBar と同じく
 * 動画の下端に置く。焼き込みのテロップや出演者カードは端から余白を取って配置されるため、
 * 最下端の細いバーとは競合しない。
 * 再生位置の唯一の手がかりでもあるので、
 * - 常時は 4px の細いバー（映像の邪魔をしない）
 * - ホバー中は 6px に太らせ、経過/総尺のタイムスタンプを出す（`showTimeStamps` 指定時）
 * という2段構えにして、視認性と控えめさを両立させる。
 * 明るい映像でも見えるよう、トラックと目盛りには影を敷く。
 */
const ProgressBarPresentation: FC<ProgressBarPresentationProps> = ({
  progress,
  buffered = 0,
  currentTimeDisplay,
  durationDisplay,
  remainingTimeDisplay,
  showTimeStamps = false,
  isSeekable = true,
  onClick,
  onMouseDown,
  seekBarRef,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      data-testid="progress-bar"
      /*
       * 下端からの持ち上げ量は呼び出し側が --moovy-seekbar-lift で指定する。
       * BOX はバーの足元にクレジットを敷くため、その高さぶん持ち上げる。
       * 指定が無ければ 0px＝従来どおり最下端。
       */
      className="absolute right-0 left-0 z-10 px-2 pb-2"
      style={{ bottom: 'var(--moovy-seekbar-lift, 0px)' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/*
       * Timestamps。常時出すと映像に文字が乗り続けるので、ホバー中だけ出す。
       *
       * バーの左端＝開始・右端＝終了なので、左に経過・右に残りを振り分けると
       * 数字とバーの位置が意味的に一致する。
       * 総尺（0:37）ではなく残り時間を出すのは、分岐動画の視聴者が知りたいのが
       * 「37秒中の27秒」ではなく「あと何秒で次を選べるか」だから。
       */}
      {showTimeStamps && (
        <div
          data-testid="timestamps"
          className={`mb-1 flex justify-between px-2 text-[12px] text-white tabular-nums drop-shadow-[0_1px_12px_rgba(0,0,0,0.9)] transition-opacity duration-150 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span>{currentTimeDisplay}</span>
          {remainingTimeDisplay ? (
            <span>
              <span className="opacity-70">残り </span>
              {remainingTimeDisplay}
            </span>
          ) : (
            <span>{durationDisplay}</span>
          )}
        </div>
      )}

      {/* Progress Bar。当たり判定はバー本体より広く取り、細いままでも掴めるようにする */}
      <div
        className={isSeekable ? 'cursor-pointer py-1.5' : 'cursor-default py-1.5'}
        onClick={onClick}
        onMouseDown={onMouseDown}
      >
        <div
          ref={seekBarRef}
          data-testid="seek-bar"
          className={`relative overflow-hidden rounded-full shadow-[0_1px_3px_rgba(0,0,0,0.5)] transition-[height] duration-150 ${
            isHovered ? 'h-[6px]' : 'h-[4px]'
          }`}
        >
          {/* Track */}
          <div data-testid="progress-track" className="absolute inset-0 bg-black/50" />

          {/* Buffered Progress */}
          {buffered > 0 && (
            <div
              data-testid="buffered-bar"
              className="absolute top-0 left-0 h-full bg-white/35"
              style={{ width: `${buffered}%` }}
            />
          )}

          {/* Progress Fill */}
          <div
            data-testid="progress-fill"
            className="absolute top-0 left-0 h-full bg-white transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default ProgressBarPresentation
